/*
 * Champ d'adresse belge avec suggestions pendant la saisie (Photon, données OpenStreetMap).
 * Une proposition choisie remplit rue, numéro, code postal et commune ; une rue sans numéro demande le numéro.
 * « Je ne trouve pas mon adresse » ouvre une saisie en quatre champs.
 */
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { CheckCircle, Loader2, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { type AddressLang, type AddressParts, type AddressSuggestion, formatAddress, parseAddress, searchAddresses } from '../lib/address';

interface Props {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
}

const inputClass = (error?: string) => `w-full px-4 py-3 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 ${
  error ? 'border-red-300 focus:ring-red-200 bg-red-50' : 'border-neutral-200 focus:ring-amber-200 focus:border-amber-400 bg-white'
}`;

export function AddressAutocomplete({ label, value, onChange, error, placeholder, required = true }: Props) {
  const { t, i18n } = useTranslation();
  const lang: AddressLang = (['fr', 'nl', 'en'] as const).find(l => (i18n.resolvedLanguage ?? i18n.language ?? 'fr').startsWith(l)) ?? 'fr';
  const tx = (key: string) => t(`join.addressField.${key}`);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [active, setActive] = useState(-1);
  /** Adresse choisie dans les propositions : affichée « vérifiée » tant qu'elle n'est pas modifiée. */
  const [verified, setVerified] = useState<string | null>(null);
  /** Rue choisie sans numéro : le numéro est demandé à part. */
  const [pendingStreet, setPendingStreet] = useState<AddressParts | null>(null);
  const [number, setNumber] = useState('');
  const [manual, setManual] = useState(false);
  const [parts, setParts] = useState<AddressParts>(() => parseAddress(value));
  const timer = useRef<number | null>(null);
  const abort = useRef<AbortController | null>(null);
  const numberRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
    abort.current?.abort();
  }, []);

  const search = (text: string) => {
    if (timer.current) window.clearTimeout(timer.current);
    abort.current?.abort();
    if (text.trim().length < 4) { setSuggestions([]); setLoading(false); return; }
    timer.current = window.setTimeout(async () => {
      const ctrl = new AbortController();
      abort.current = ctrl;
      setLoading(true);
      try {
        const list = await searchAddresses(text.trim(), ctrl.signal, lang);
        if (ctrl.signal.aborted) return;
        setSuggestions(list);
        setFailed(false);
        setActive(list.length ? 0 : -1);
        setOpen(true);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') { setFailed(true); setSuggestions([]); setOpen(true); }
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 300);
  };

  const type = (text: string) => {
    onChange(text);
    setPendingStreet(null);
    search(text);
  };

  const choose = (s: AddressSuggestion) => {
    setOpen(false);
    setSuggestions([]);
    if (s.complete) {
      onChange(s.label);
      setVerified(s.label);
      setPendingStreet(null);
      return;
    }
    // Rue trouvée sans numéro : on garde la rue et la commune, le numéro est demandé.
    setPendingStreet(s.parts);
    setNumber('');
    onChange(formatAddress({ ...s.parts, number: '' }));
    setTimeout(() => numberRef.current?.focus(), 0);
  };

  const confirmNumber = () => {
    if (!pendingStreet || !number.trim()) return;
    const full = formatAddress({ ...pendingStreet, number: number.trim() });
    onChange(full);
    setVerified(full);
    setPendingStreet(null);
  };

  const setPart = (patch: Partial<AddressParts>) => {
    const next = { ...parts, ...patch };
    setParts(next);
    onChange(formatAddress(next));
  };

  const onKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (ev.key === 'ArrowDown') { ev.preventDefault(); setActive(i => (i + 1) % suggestions.length); }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); setActive(i => (i <= 0 ? suggestions.length - 1 : i - 1)); }
    else if (ev.key === 'Enter' && active >= 0) { ev.preventDefault(); choose(suggestions[active]); }
    else if (ev.key === 'Escape') setOpen(false);
  };

  const isVerified = !!verified && verified === value;

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-800 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      {manual ? (
        <div className="grid grid-cols-4 gap-2">
          <input aria-label={tx('street')} value={parts.street} onChange={ev => setPart({ street: ev.target.value })} placeholder={tx('street')}
            className={`${inputClass(error)} col-span-3`} />
          <input aria-label={tx('number')} value={parts.number} onChange={ev => setPart({ number: ev.target.value })} placeholder={tx('numberShort')}
            className={inputClass(error)} />
          <input aria-label={tx('postcode')} value={parts.postcode} inputMode="numeric" maxLength={4}
            onChange={ev => setPart({ postcode: ev.target.value.replace(/\D/g, '') })} placeholder={tx('postcode')} className={inputClass(error)} />
          <input aria-label={tx('city')} value={parts.city} onChange={ev => setPart({ city: ev.target.value })} placeholder={tx('city')}
            className={`${inputClass(error)} col-span-3`} />
        </div>
      ) : (
        <div className="relative">
          <div className="relative">
            <MapPin className="w-4 h-4 text-neutral-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={value}
              onChange={ev => type(ev.target.value)}
              onKeyDown={onKeyDown}
              onFocus={() => suggestions.length > 0 && setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              placeholder={placeholder ?? tx('placeholder')}
              autoComplete="off"
              role="combobox"
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              className={`${inputClass(error)} pl-10 pr-10`}
            />
            {loading && <Loader2 className="w-4 h-4 text-neutral-400 animate-spin absolute right-3.5 top-1/2 -translate-y-1/2" />}
          </div>
          {open && (
            <div id={listId} role="listbox" className="absolute z-30 left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-xl shadow-lg overflow-hidden">
              {failed && <p className="px-4 py-3 text-xs text-neutral-500">{tx('unavailable')}</p>}
              {!failed && suggestions.length === 0 && <p className="px-4 py-3 text-xs text-neutral-500">{tx('noResult')}</p>}
              {suggestions.map((s, i) => (
                <button
                  key={s.key}
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  onMouseDown={ev => { ev.preventDefault(); choose(s); }}
                  onMouseEnter={() => setActive(i)}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-3 ${i === active ? 'bg-amber-50' : 'bg-white'}`}
                >
                  <span className="text-neutral-800">{s.label}</span>
                  {!s.complete && <span className="text-[11px] text-neutral-400 whitespace-nowrap">{tx('plusNumber')}</span>}
                </button>
              ))}
              <div className="flex items-center justify-between gap-3 px-4 py-2 border-t border-neutral-100 bg-neutral-50">
                <button type="button" onMouseDown={ev => { ev.preventDefault(); setParts(parseAddress(value)); setManual(true); setOpen(false); }}
                  className="text-xs font-medium text-amber-700 hover:text-amber-800">
                  {tx('notFound')}
                </button>
                <span className="text-[10px] text-neutral-400">{tx('osm')}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {pendingStreet && !manual && (
        <div className="mt-2 flex items-center gap-2">
          <input
            ref={numberRef}
            value={number}
            onChange={ev => setNumber(ev.target.value)}
            onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); confirmNumber(); } }}
            placeholder={tx('numberPh')}
            aria-label={tx('number')}
            className={`${inputClass()} max-w-[180px]`}
          />
          <button type="button" onClick={confirmNumber} disabled={!number.trim()}
            className="px-4 py-3 rounded-xl text-sm font-semibold disabled:opacity-40" style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}>
            {tx('addNumber')}
          </button>
        </div>
      )}

      {isVerified && !error && (
        <p className="text-xs text-green-700 mt-1 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" />{tx('verified')}</p>
      )}
      {manual ? (
        <button type="button" onClick={() => { setManual(false); search(value); }} className="text-xs text-neutral-500 hover:text-neutral-700 mt-1">
          {tx('back')}
        </button>
      ) : !isVerified && !error && !pendingStreet && (
        <p className="text-xs text-neutral-400 mt-1">{tx('choose')}</p>
      )}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
